import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { inr, fmtDate } from "@/utils";
import { toast } from "sonner";

function downloadCSV(filename, rows) {
  if (!rows.length) { toast.error("Nothing to export"); return; }
  const cols = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: expenses = [] } = useQuery({ queryKey: ["expenses"], queryFn: async () => (await api.get("/expenses")).data });
  const { data: incomes = [] } = useQuery({ queryKey: ["incomes"], queryFn: async () => (await api.get("/incomes")).data });
  const { data: leads = [] } = useQuery({ queryKey: ["leads"], queryFn: async () => (await api.get("/leads")).data });
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });

  const rev = useMemo(() => incomes.reduce((s, r) => s + (r.amount || 0), 0), [incomes]);
  const exp = useMemo(() => expenses.reduce((s, r) => s + (r.amount || 0), 0), [expenses]);

  const reportCards = [
    { label: "Projects Master", count: projects.length, cta: () => downloadCSV("projects.csv", projects.map((p) => ({ code: p.code, name: p.name, location: p.location, ownership: p.ownership_type, est_cost: p.estimated_cost, est_revenue: p.estimated_revenue, rera: p.rera_number }))) },
    { label: "Income Ledger", count: incomes.length, cta: () => downloadCSV("incomes.csv", incomes.map((i) => ({ date: i.date, source: i.source, amount: i.amount, gst: i.gst_amount, description: i.description }))) },
    { label: "Expense Ledger", count: expenses.length, cta: () => downloadCSV("expenses.csv", expenses.map((e) => ({ date: e.date, category: e.category, vendor: e.vendor_name, amount: e.amount, gst: e.gst_amount, tds: e.tds_amount, jda: e.is_land_owner_payment ? "yes" : "no", description: e.description }))) },
    { label: "Customer Book", count: customers.length, cta: () => downloadCSV("customers.csv", customers.map((c) => ({ name: c.name, phone: c.phone, pan: c.pan, total: c.total_amount, paid: c.paid_amount, outstanding: c.outstanding, booking: c.booking_date, status: c.status }))) },
    { label: "Lead Pipeline", count: leads.length, cta: () => downloadCSV("leads.csv", leads.map((l) => ({ name: l.name, phone: l.phone, email: l.email, source: l.source, status: l.status, budget: l.budget, score: l.score, tier: l.score_tier }))) },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Insights</div>
        <h1 className="font-heading text-3xl font-bold mt-1">Reports Centre</h1>
        <p className="text-sm text-zinc-500 mt-1">Export any dataset to CSV for Excel, Tally or auditor.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Revenue</div><div className="kpi-num mt-1 text-emerald-700">{inr(rev)}</div></CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Expense</div><div className="kpi-num mt-1 text-red-600">{inr(exp)}</div></CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Gross Profit</div><div className="kpi-num mt-1">{inr(rev - exp)}</div></CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Reports Available</div><div className="kpi-num mt-1">{reportCards.length}</div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reportCards.map((r) => (
          <Card key={r.label} className="bg-white border-zinc-200 hover:border-emerald-400 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base">{r.label}</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-between">
              <div className="text-sm text-zinc-500">{r.count} rows</div>
              <Button size="sm" variant="outline" onClick={r.cta} data-testid={`export-${r.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <Download size={13} className="mr-1" /> CSV
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
